/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * US NANP phone number regex. Validates the normalized `NNN-NNN-NNNN` or bare
 * `NNNNNNNNNN` form (dashes optional) after country-code stripping. Rules:
 *   - Area code: first digit 2–9, second digit 0–8, third digit 0–9
 *   - Exchange: first digit 2–9
 *   - Optional extension: `x<digits>` (with optional leading whitespace)
 *
 * Callers must strip country-code prefixes and non-digit/non-dash formatting
 * (parentheses, spaces) before testing — see `createShippingAddressSchema` and
 * `createCustomerProfileFormSchema` for the canonical normalization pattern.
 */
export const US_PHONE_REGEX = /^([2-9][0-8][0-9])-?([2-9][0-9]{2})-?([0-9]{4})(\s*x[0-9]+)?$/;

/**
 * Extracts country code from a phone number.
 * Returns '+1' as default if no country code is found.
 *
 * @example
 * extractCountryCode("+1 (123) 456-7890") // "+1"
 * extractCountryCode("+44 1234567890")    // "+44"
 * extractCountryCode("(123) 456-7890")    // "+1" (default)
 */
export const extractCountryCode = (phone: string): string => {
    const match = phone.match(/^(\+\d+)/);
    return match ? match[1] : '+1';
};

/**
 * Strips country code prefix from a phone number.
 * Removes leading "+" followed by country code and optional space/dash delimiter.
 *
 * @example
 * stripCountryCode("+1 (123) 456-7890") // "(123) 456-7890"
 * stripCountryCode("(123) 456-7890")    // "(123) 456-7890"
 * stripCountryCode("+11234567890")      // "1234567890" (strips +1, keeps 1234567890)
 * stripCountryCode("+1(123)456-7890")   // "(123)456-7890" (strips +1, keeps opening paren)
 * stripCountryCode("+1234 567-890")     // "567-890" (strips +1234 with space)
 */
export const stripCountryCode = (phone: string): string => {
    if (!phone.startsWith('+')) return phone;

    // Match + followed by:
    // 1. 1-4 digits followed by space or dash: \d{1,4}[\s-]
    // 2. OR exactly 1 digit when followed by 10 more digits: \d(?=\d{10})
    // 3. OR 1-3 digits with no delimiter (fallback for cases like +1(123)...): \d{1,3}
    return phone.replace(/^\+(?:\d{1,4}[\s-]|\d(?=\d{10})|\d{1,3})/, '');
};

/**
 * Strips all non-digit characters from a string.
 *
 * @example
 * stripNonDigits("(123) 456-7890") // "1234567890"
 * stripNonDigits("abc123")         // "123"
 */
export const stripNonDigits = (value: string): string => value.replace(/\D/g, '');

/**
 * Formats phone number input as user types (US format).
 * Automatically adds parentheses and dashes: (123) 456-7890
 * Limits input to 10 digits.
 *
 * @example
 * formatPhoneInput("1234567890") // "(123) 456-7890"
 * formatPhoneInput("123456")     // "(123) 456"
 * formatPhoneInput("123")        // "(123"
 */
export const formatPhoneInput = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 10);

    if (limited.length >= 7) {
        return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    } else if (limited.length >= 4) {
        return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else if (limited.length > 0) {
        return `(${limited}`;
    }
    return limited;
};

/**
 * Formats phone number for display with country code (read-only/summary view).
 * Strips any existing country code prefix, then formats as: +1 (123) 456-7890
 *
 * @param phone - Phone number to format
 * @param countryCode - Country code to prepend (default: '+1')
 * @example
 * formatPhoneDisplay("1234567890")        // "+1 (123) 456-7890"
 * formatPhoneDisplay("+1 1234567890")     // "+1 (123) 456-7890" (strips existing prefix)
 * formatPhoneDisplay("11234567890")       // "+1 (123) 456-7890" (handles 11-digit with leading 1)
 */
export const formatPhoneDisplay = (phone: string, countryCode = '+1'): string => {
    const cleanPhone = stripCountryCode(phone);

    const digits = cleanPhone.replace(/\D/g, '');
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(0, 10);

    if (local.length === 10) {
        return `${countryCode} (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
    }
    return cleanPhone;
};

/**
 * Normalizes a phone number for **persistence** on an SFCC OrderAddress /
 * CustomerAddress record so the stored value matches legacy SFRA (e.g.
 * `813-813-8138`), regardless of what the shopper typed or the presentation-
 * layer formatter produced (e.g. `+1 (813) 813-8138`, `(813) 813-8138`,
 * `+1 813 813-8138`).
 *
 * Rules:
 *   - Strip every non-digit character from the input.
 *   - If the digit string is 10 digits, format as `NNN-NNN-NNNN`.
 *   - If the digit string is 11 digits starting with `1` (US country code
 *     included), drop the leading `1` and format the trailing 10 as
 *     `NNN-NNN-NNNN`.
 *   - Anything else (empty, non-10-digit international, partial) is returned
 *     unchanged so we do not silently mangle a legitimately-different number.
 *
 * `undefined` / `null` / empty input is passed through untouched so callers
 * can spread the normalized value into an OrderAddress body without inflating
 * an optional field to an empty string.
 *
 * NOT for display — see {@link formatPhoneDisplay} / {@link formatPhoneCompact}
 * for the UI-facing formatters.
 *
 * @example
 * normalizeUsPhone('+1 (813) 813-8138') // '813-813-8138'
 * normalizeUsPhone('(813) 813-8138')    // '813-813-8138'
 * normalizeUsPhone('18138138138')       // '813-813-8138'
 * normalizeUsPhone('+44 20 7946 0018')  // '+44 20 7946 0018' (non-10-digit → passthrough)
 * normalizeUsPhone('')                  // ''
 * normalizeUsPhone(undefined)           // undefined
 */
export const normalizeUsPhone = <T extends string | undefined | null>(phone: T): T => {
    if (phone == null || phone === '') return phone;
    const digits = String(phone).replace(/\D/g, '');
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (local.length !== 10) {
        return phone;
    }
    return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}` as T;
};

/**
 * Formats a 10-digit phone number compactly for display, without parentheses:
 *   `+1 888 123-4567`
 *
 * Used by the checkout saved-address cards (see Figma) where the design calls
 * for a space-separated 3-3-4 grouping prefixed with the country code, rather
 * than the parenthesised `+1 (888) 123-4567` produced by `formatPhoneDisplay`.
 *
 * Falls back to the country-code-stripped raw value when the input is not a
 * recognisable 10-digit (or 11-digit-leading-1) number — keeps the UI honest
 * about partial / non-US numbers instead of silently mangling them.
 *
 * @example
 * formatPhoneCompact('8881234567')            // "+1 888 123-4567"
 * formatPhoneCompact('+1 (888) 123-4567')     // "+1 888 123-4567"
 * formatPhoneCompact('18881234567')           // "+1 888 123-4567"
 * formatPhoneCompact('8881234567', '+44')     // "+44 888 123-4567"
 * formatPhoneCompact('123')                   // "123"  (not formattable)
 */
export const formatPhoneCompact = (phone: string, countryCode = '+1'): string => {
    if (!phone) return phone;

    const cleanPhone = stripCountryCode(phone);
    const digits = cleanPhone.replace(/\D/g, '');
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(0, 10);

    if (local.length === 10) {
        return `${countryCode} ${local.slice(0, 3)} ${local.slice(3, 6)}-${local.slice(6)}`;
    }
    return cleanPhone;
};
